import {Panel, PanelType} from '@fluentui/react';
import {useBoolean} from '@fluentui/react-hooks';
import {useRegulatoryDocumentsContext} from 'features/RegulatoryDocuments/context';
import {useCommand} from 'hooks';
import React, {useContext} from 'react';
import {useTranslation} from 'react-i18next';
import {
	EditParagraphsFormSubmissionStatusContext,
	EditParagraphsFormSubmissionStatusContextValue,
} from 'features/RegulatoryDocuments/RegDocDetailsPage/EditParagraphsFormSubmissionStatusProvider';
import {
	ComparisonComponent,
	ComparisonComponentOnTransferredType,
} from './ComparisonComponent/ComparisonComponent';
import {UserRole} from 'types';

export interface CompareVersionPanelProps {
	regulationId: string;
	onTransferred?: ComparisonComponentOnTransferredType;
}

export const CompareVersionPanel: React.FC<CompareVersionPanelProps> = ({
	regulationId,
	onTransferred,
}) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'CompareVersionPanel',
	});
	const {selectedRegulatoryDocument, allSelectedRegulatoryDocuments} =
		useRegulatoryDocumentsContext();

	/**
	 * Other
	 */
	const submissionStatus: EditParagraphsFormSubmissionStatusContextValue =
		useContext(EditParagraphsFormSubmissionStatusContext);

	const [isOpen, {setTrue: openPanel, setFalse: dismissPanel}] =
		useBoolean(false);

	const isSubmitting: boolean = submissionStatus?.isSubmitting ?? false;

	useCommand(
		{
			key: 'compare',
			text: t('CompareVersion'),
			ariaLabel: 'compare versions',
			priority: 3,
			iconProps: {
				iconName: 'BranchCompare',
			},
			disabled: isSubmitting,
			onClick: openPanel,
			roles: [
				UserRole.SystemAdministrator,
				UserRole.Vko,
				UserRole.VkoExternal,
				UserRole.Vex,
				UserRole.ShApprover,
				UserRole.Readers,
				UserRole.RegulationReader,
				UserRole.Bav,
				UserRole.Bedarfskoordinator,
				UserRole.Avk,
				UserRole.VexSupport,
			],
		},
		[selectedRegulatoryDocument, allSelectedRegulatoryDocuments, isSubmitting],
	);

	/**
	 * We wait for the paragraphs to load so that the user doesn't copy a
	 * paragraph's outdated metadata to another paragraph.
	 */
	return (
		<Panel
			isLightDismiss
			styles={{content: {paddingRight: 0}}}
			isOpen={isOpen}
			onDismiss={dismissPanel}
			closeButtonAriaLabel='Close'
			headerText={t('CompareVersions')}
			type={PanelType.extraLarge}
			isHiddenOnDismiss={false}
		>
			<ComparisonComponent
				regulationId={regulationId}
				onTransferred={(regDocId, items, selectedFields) =>
					onTransferred?.(regDocId, items, selectedFields)
				}
			/>
		</Panel>
	);
};
